// Deriving an `output_schema` from the paths a spec already reads.
//
// The whole migration off `format_prompt` rests on this, and the reason it can rest on it is that
// converting a prose spec is NOT a prose-parsing problem. `output_extraction` already names every
// node the answer has to contain — one `scores[].jsonpath` plus each `extra_columns` path — so the
// shape is derived, not guessed.
//
// And it is checkable: `matchCount` runs the loader's own coherence rule, so a caller can PROVE
// every score path resolves to exactly one node of what came back rather than asserting it.
//
// Three things it refuses to do:
//
//   * Invent a shape for a path it cannot read. `parseJsonPath` models the dotted / bracket-quoted /
//     indexed subset and returns null for filters, wildcards and slices; those paths come back in
//     `unresolvable` and the caller sends that spec somewhere else.
//   * Resolve a conflict. If one path says `$.A` is a value and another says `$.A.B` exists, `A`
//     cannot be both a leaf and an object — that is reported, not decided.
//   * Pretend to know a leaf's type. Every leaf is a DEFAULT, listed in `defaults` so the caller can
//     show what was assumed and let the operator retype it.

import { parseJsonPath } from './jsonPath';

// A score is a number far more often than not, and an extra column is prose. Both are shown as
// assumptions rather than applied silently.
const SCORE_LEAF_TYPE = 'number';
const COLUMN_LEAF_TYPE = 'string';

const isIndex = (segment) => /^\d+$/.test(String(segment));

// The node tree is built as a mutable intermediate and only then rendered with the strict-mode
// invariants applied, because an object's `required` list cannot be written until every path that
// contributes a property has been walked.
const emptyNode = () => ({ kind: null, children: {}, order: [], items: null, leafType: null });

const render = (node) => {
  if (node.kind === 'leaf') return { type: node.leafType };
  if (node.kind === 'array') {
    // Strict mode requires `items` on every array. An array with no known element shape cannot
    // happen here: a numeric segment is only produced by a path that continues past it.
    return { type: 'array', items: render(node.items) };
  }
  const properties = {};
  node.order.forEach((key) => {
    properties[key] = render(node.children[key]);
  });
  return {
    type: 'object',
    properties,
    // Every property, because strict mode allows no optional keys.
    required: node.order.slice(),
    additionalProperties: false,
  };
};

const renderPathLabel = (segments) =>
  `$${segments.map((s) => (/^[A-Za-z_$][\w$]*$/.test(s) ? `.${s}` : `['${s}']`)).join('')}`;

// `entries` is `[{ jsonpath, leafType, label }]`. Returns the schema, the paths it could not use, the
// conflicts it refused to resolve, and every leaf type it defaulted.
const build = (entries) => {
  const root = emptyNode();
  root.kind = 'object';
  const unresolvable = [];
  const conflicts = [];
  const defaults = [];

  entries.forEach((entry) => {
    const segments = parseJsonPath(entry.jsonpath);
    if (!segments || segments.length === 0) {
      unresolvable.push({ ...entry, reason: 'outside the path forms this client can read' });
      return;
    }
    if (isIndex(segments[0])) {
      unresolvable.push({ ...entry, reason: 'the top level of an answer is an object, not an array' });
      return;
    }

    let node = root;
    let ok = true;

    for (let i = 0; i < segments.length && ok; i += 1) {
      const segment = segments[i];
      const isLast = i === segments.length - 1;
      const nextIsIndex = !isLast && isIndex(segments[i + 1]);

      if (isIndex(segment)) continue; // consumed by the parent that became an array

      if (node.kind === 'leaf') {
        conflicts.push({
          ...entry,
          reason: `\`${renderPathLabel(
            segments.slice(0, i)
          )}\` is read as a value by another path, so it cannot also contain \`${segment}\``,
        });
        ok = false;
        break;
      }
      node.kind = 'object';

      if (!node.children[segment]) {
        node.children[segment] = emptyNode();
        node.order.push(segment);
      }
      const child = node.children[segment];

      if (isLast) {
        if (child.kind === 'object' || child.kind === 'array') {
          conflicts.push({
            ...entry,
            reason: `\`${entry.jsonpath}\` is read as a value here, but another path reads inside it`,
          });
          ok = false;
          break;
        }
        child.kind = 'leaf';
        child.leafType = entry.leafType;
        defaults.push({ jsonpath: entry.jsonpath, label: entry.label, type: entry.leafType });
        break;
      }

      if (nextIsIndex) {
        if (child.kind === 'leaf') {
          conflicts.push({ ...entry, reason: `\`${segment}\` is read as a value by another path` });
          ok = false;
          break;
        }
        child.kind = 'array';
        if (!child.items) child.items = emptyNode();
        node = child.items;
      } else {
        node = child;
      }
    }
  });

  return { root, unresolvable, conflicts, defaults };
};

// `output` is an `output_extraction` — post-desugar, so `scores` rather than `score_jsonpath`.
// Per-score `extra_columns` are included: they are coherence-checked against the schema too, so a
// derivation that ignored them would produce a shape the loader rejects.
export const deriveSchema = (output) => {
  const source = output && typeof output === 'object' ? output : {};
  const entries = [];

  (Array.isArray(source.scores) ? source.scores : []).forEach((score) => {
    if (!score || !score.jsonpath) return;
    entries.push({
      jsonpath: score.jsonpath,
      leafType: SCORE_LEAF_TYPE,
      label: `score \`${score.name || 'unnamed'}\``,
    });
    const scoped = score.extra_columns;
    if (scoped && typeof scoped === 'object') {
      Object.keys(scoped).forEach((name) => {
        entries.push({ jsonpath: scoped[name], leafType: COLUMN_LEAF_TYPE, label: `column \`${name}\`` });
      });
    }
  });

  const specLevel = source.extra_columns;
  if (specLevel && typeof specLevel === 'object') {
    Object.keys(specLevel).forEach((name) => {
      entries.push({ jsonpath: specLevel[name], leafType: COLUMN_LEAF_TYPE, label: `column \`${name}\`` });
    });
  }

  if (entries.length === 0) {
    return {
      schema: null,
      unresolvable: [],
      conflicts: [],
      defaults: [],
      blocked: 'This spec reads nothing, so there is no shape to derive. Declare a score first.',
    };
  }

  const { root, unresolvable, conflicts, defaults } = build(entries);
  const usable = root.order.length > 0 && conflicts.length === 0;

  return {
    schema: usable ? render(root) : null,
    unresolvable,
    conflicts,
    defaults,
    blocked: usable
      ? null
      : conflicts.length > 0
      ? 'Two paths disagree about the shape, so it cannot be derived — see below.'
      : 'None of this spec’s paths can be read by this client, so the shape has to be written by hand.',
  };
};
